import React, { useState, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import Avatar from "../../../../../../../../../assets/images/avatarr.png";
import capitalize from "../../../../../../../../../capitalize";

// Shared hooks
import useFetchCategories from "./shared/useFetchCategories";
import useFetchPricing from "./shared/useFetchPricing";
import useCategoryForm from "./shared/useCategoryForm";

// Shared components
import CategoryDropdown from "./shared/CategoryDropdown";

// Dropoff-specific
import useDropOffTransaction from "./hooks/useDropOffTransaction";
import CategoryRow from "./comps/CategoryRow";
import DropOffFooter from "./comps/DropOffFooter";

const NewDropCodeModal = ({
  showDrop,
  handleCloseDrop,
  dropOffData,
  handleShowAwaiting,
  setSteps,
  handleShowPaymentR,
  getDropOff,
  isManual,
}) => {
  // ─── Categories from API ──────────────────────────────────────────────────
  const { categories, loading: categoriesLoading, error: categoriesError } =
    useFetchCategories();

  const { pricingData } = useFetchPricing();

  const {
    categoryData,
    updateCategory,
    updateKg,
    computedAmounts,
    totals,
    hasErrors,
    buildWastePayload,
    resetForm,
  } = useCategoryForm(categories, pricingData, "dropoff");

  // ─── UI state ─────────────────────────────────────────────────────────────
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState("");

  const isManualEnabled = isManual === "true";

  // ─── Category selection ───────────────────────────────────────────────────
  // Unchecking a category must also clear its categoryData — buildWastePayload()
  // and totals filter purely by kg > 0 with no knowledge of selectedCategories,
  // so a stale kg value would keep an unchecked category in the payload/total.
  const handleCheckboxChange = useCallback(
    (label) => {
      setSelectedCategories((prev) => {
        const isRemoving = prev.includes(label);
        if (isRemoving) {
          const cat = categories.find((c) => c.label === label);
          if (cat) {
            updateCategory(cat.id, "kg", "");
            updateCategory(cat.id, "deduction", "");
            updateCategory(cat.id, "manualPrice", "");
            updateCategory(cat.id, "isManualMode", false);
            updateCategory(cat.id, "showDeduction", false);
            updateCategory(cat.id, "kgError", null);
          }
          return prev.filter((c) => c !== label);
        }
        return [...prev, label];
      });
    },
    [categories, updateCategory]
  );

  // ─── Full reset ───────────────────────────────────────────────────────────
  const handleReset = useCallback(() => {
    resetForm();
    setSelectedCategories([]);
    setSelectedMethod("");
  }, [resetForm]);

  // ─── Transaction success handlers ─────────────────────────────────────────
  const handleSuccessUnregistered = useCallback(() => {
    getDropOff(dropOffData?.code);
    handleReset();
    handleCloseDrop();
    handleShowPaymentR();
  }, [getDropOff, dropOffData?.code, handleReset, handleCloseDrop, handleShowPaymentR]);

  const handleSuccessRegistered = useCallback(
    (responseData) => {
      localStorage.setItem("dropOff", JSON.stringify(responseData));
      getDropOff(dropOffData?.code);
      setSteps(1);
      handleReset();
      handleCloseDrop();
      handleShowAwaiting();
    },
    [getDropOff, dropOffData?.code, setSteps, handleReset, handleCloseDrop, handleShowAwaiting]
  );

  // ─── Transaction hook ─────────────────────────────────────────────────────
  const { loading, initiateTransaction } = useDropOffTransaction({
    dropOffData,
    onSuccessUnregistered: handleSuccessUnregistered,
    onSuccessRegistered: handleSuccessRegistered,
  });

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(() => {
    initiateTransaction({
      wastePayload: buildWastePayload(),
      paymentMethod: selectedMethod,
      totalManual: isManualEnabled ? totals.totalAmount : 0,
      hasErrors,
    });
  }, [initiateTransaction, buildWastePayload, selectedMethod, isManualEnabled, totals.totalAmount, hasErrors]);

  // ─── Modal close ──────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    handleReset();
    handleCloseDrop();
  }, [handleReset, handleCloseDrop]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Modal
      show={showDrop}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton />

      <Modal.Body>
        <div className="text-left ml-3">
          {/* ── Header ── */}
          <div className="mb-2">
            <h5 className="sec-headers mb-3">Weigh Used Plastics</h5>

            <div className="d-flex justify-content-between align-items-center">
              <p className="ftsz-regular" style={{ fontSize: "14px" }}>
                {capitalize(dropOffData?.owner?.roles?.[0])}:{" "}
                {dropOffData?.owner?.name}
              </p>

              <div className="d-flex align-items-center">
                <div>
                  <p className="mb-0" style={{ fontSize: "14px", color: "#898989" }}>
                    Drop off Code
                  </p>
                  <p style={{ fontSize: "14px", color: "#404040", fontWeight: "700" }}>
                    {dropOffData?.code}
                  </p>
                </div>
                <div className="imgAvatarHolder ml-3">
                  <img src={Avatar} alt="avatar" />
                </div>
              </div>
            </div>

            <p className="ftsz-regular" style={{ fontSize: "14px" }}>
              Used Plastic Categories
            </p>
          </div>

          {/* ── Loading / error states ── */}
          {categoriesLoading && (
            <p className="text-muted" style={{ fontSize: "14px" }}>
              Loading categories...
            </p>
          )}

          {categoriesError && (
            <p className="text-danger" style={{ fontSize: "14px" }}>
              {categoriesError}
            </p>
          )}

          {/* ── Main form ── */}
          {!categoriesLoading && !categoriesError && (
            <>
              <CategoryDropdown
                categories={categories}
                selected={selectedCategories}
                onChange={handleCheckboxChange}
              />

              {categories
                .filter((cat) => selectedCategories.includes(cat.label))
                .map((cat) => (
                  <CategoryRow
                    key={cat.id}
                    category={cat}
                    data={categoryData[cat.id]}
                    computed={computedAmounts[cat.id]}
                    isManualEnabled={isManualEnabled}
                    onChange={(field, value) =>
                      updateCategory(cat.id, field, value)
                    }
                    onKgChange={(value) => updateKg(cat.id, value)}
                    onToggleManual={() =>
                      updateCategory(
                        cat.id,
                        "isManualMode",
                        !categoryData[cat.id]?.isManualMode
                      )
                    }
                  />
                ))}

              <DropOffFooter
                totalAmount={totals.totalAmount}
                totalDeduction={totals.totalDeduction}
                isManualEnabled={isManualEnabled}
                selectedMethod={selectedMethod}
                onMethodChange={setSelectedMethod}
                dropOffData={dropOffData}
                onSubmit={handleSubmit}
                loading={loading}
                disabled={selectedCategories.length === 0 || hasErrors}
              />
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default NewDropCodeModal;
